// Shared bill-total math for legacy single-finalize jobs (jobs that
// went through Finalize Bill & Close Job before progress billing
// existed, or T&M jobs that ran today's default flow). Used by:
//   - InvoicesPage (the live invoices list)
//   - dashboard/computeDashboardData (Invoices quadrant mini-view)
// Both must produce identical numbers or the dashboard mini-view
// disagrees with the page it links to.
//
// Quoted jobs return the locked quoted price as grandTotal; T&M jobs
// sum labor (hours x rate) + materials + expenses + fuel with their
// respective markups, then apply org-level tax lines against the
// taxable subset of those.

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Returns true when the customer is tax-exempt AND their exemption cert
// expiration date is in the past. Used to surface the red warning
// banner on the customer card / quote editor / bill editor / etc. AND
// to gate the effective-exempt computation (see isEffectivelyTaxExempt
// below).
export const isTaxCertExpired = (customer) => {
  if (!customer?.taxExempt) return false;
  const exp = customer.taxExemptCertExpires;
  if (!exp) return false;
  const today = new Date().toISOString().slice(0, 10);
  return exp < today;
};

// Returns true when the customer SHOULD be charged $0 tax right now.
// taxExempt flag alone isn't enough — an expired cert means the
// exemption is legally invalid, so tax IS owed until the customer
// renews their certificate. Use this everywhere tax math decides
// whether to zero out the tax block (NOT for UI banners — those still
// want raw taxExempt + isTaxCertExpired so the warning shows even
// though tax is being applied).
export const isEffectivelyTaxExempt = (customer) => {
  if (!customer?.taxExempt) return false;
  if (isTaxCertExpired(customer)) return false;
  return true;
};

export const computeLegacyJobTotal = (job, customer) => {
  const c = job.costing || {};
  const labor = (c.labor || []).reduce(
    (s, l) => s + num(l.hours) * num(l.rate),
    0
  );
  const materials = (c.materials || []).reduce(
    (s, m) => s + num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100),
    0
  );
  const expenses = (c.expenses || []).reduce(
    (s, e) => s + num(e.amount) * (1 + num(e.markup) / 100),
    0
  );
  const fuel = (c.fuel || []).reduce((s, f) => {
    const lc =
      f.mode === "flat"
        ? num(f.amount)
        : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
    return s + lc * (1 + num(f.markup) / 100);
  }, 0);
  const subtotal = labor + materials + expenses + fuel;

  // Quoted jobs: customer pays the locked quoted price regardless of
  // line totals.
  if (c.priceMode === "quoted") {
    return { subtotal, taxes: [], grandTotal: num(c.quotedAmount) };
  }

  // Tax-exempt status decision (pre-QB runway #6):
  //   - Finalized bills carry a snapshot of the customer's exempt status
  //     at the moment of finalization (job.billTaxExemptAtFinalize). That
  //     snapshot wins so historical invoices stay immutable when the
  //     customer's status changes later (cert expires, new cert approved,
  //     wrong status discovered). Protects QB sync from retroactively
  //     changing tax on already-issued invoice numbers.
  //   - Drafts and pre-snapshot bills (anything without the field set)
  //     read live customer status — that's correct: they aren't issued
  //     yet, so the latest status should flow through.
  const exemptAtFinalize =
    job.billFinalized && typeof job.billTaxExemptAtFinalize === "boolean"
      ? job.billTaxExemptAtFinalize
      : null;
  const exemptForThisBill =
    exemptAtFinalize !== null
      ? exemptAtFinalize
      : isEffectivelyTaxExempt(customer);
  if (exemptForThisBill) {
    return { subtotal, taxes: [], grandTotal: subtotal };
  }

  const isTaxable = (line) => line && line.taxable !== false;
  const taxableSubtotal =
    (c.labor || []).reduce(
      (s, l) => (isTaxable(l) ? s + num(l.hours) * num(l.rate) : s),
      0
    ) +
    (c.materials || []).reduce(
      (s, m) =>
        isTaxable(m)
          ? s + num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100)
          : s,
      0
    ) +
    (c.expenses || []).reduce(
      (s, e) =>
        isTaxable(e) ? s + num(e.amount) * (1 + num(e.markup) / 100) : s,
      0
    ) +
    (c.fuel || []).reduce((s, f) => {
      if (!isTaxable(f)) return s;
      const lc =
        f.mode === "flat"
          ? num(f.amount)
          : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
      return s + lc * (1 + num(f.markup) / 100);
    }, 0);
  const taxes = (c.taxes || []).map((t) => ({
    name: t.name || "Tax",
    mode: t.mode || "percent",
    rate: t.rate || "",
    amount:
      t.mode === "flat"
        ? num(t.amount)
        : taxableSubtotal * (num(t.rate) / 100),
  }));
  const taxTotal = taxes.reduce((s, t) => s + t.amount, 0);
  return { subtotal, taxes, grandTotal: subtotal + taxTotal };
};
